/**
 * Focused assignee pickers used by TaskDrawer and NewTaskDialog.
 *
 * TradePicker  — selects a trade category (assignedTradeId)
 * ContactPicker — selects a project contact (assignedToContactId)
 *
 * Both are independent: selecting one slot never clears the other.
 */
import { Check, ChevronsUpDown } from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { TradeCategory } from "@/hooks/use-config";
import type { Contact } from "@/hooks/use-contacts";
import { cn } from "@/lib/utils";

// ── Shared initials avatar ───────────────────────────────────────────

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts
    .map((p) => p.charAt(0))
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function Initials({ name }: { name: string }) {
  return (
    <span
      className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-[var(--border-2)] bg-[var(--paper-1)] text-[10px] font-medium text-[var(--text-2)] shrink-0"
      aria-hidden
    >
      {getInitials(name)}
    </span>
  );
}

// ── TradePicker ──────────────────────────────────────────────────────

export function TradePicker({
  value,
  trades,
  onChange,
  placeholder = "Assign trade…",
}: {
  value: string | null;
  trades: TradeCategory[];
  onChange: (id: string | null) => void;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);

  const sorted = useMemo(
    () =>
      [...trades].filter((t) => t.isActive !== false).sort((a, b) => a.name.localeCompare(b.name)),
    [trades],
  );

  const selected = value ? sorted.find((t) => t.id === value) : null;
  const label = selected?.name ?? null;

  function select(id: string | null) {
    onChange(id);
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-auto min-h-[30px] w-full justify-between border-[var(--border-1)] bg-[var(--paper-0)] px-2 py-1 text-[12.5px] font-normal shadow-none hover:bg-[var(--paper-1)]"
        >
          <span className="flex items-center gap-2 truncate">
            {label ? (
              <>
                <Initials name={label} />
                <span className="truncate">{label}</span>
              </>
            ) : (
              <span className="text-[var(--text-4)]">{placeholder}</span>
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start" side="bottom" avoidCollisions>
        <Command>
          <CommandInput placeholder="Search trades…" />
          <CommandList>
            <CommandEmpty>No trades found.</CommandEmpty>
            <CommandGroup>
              <CommandItem value="none" onSelect={() => select(null)}>
                <Check className={cn("mr-2 h-3.5 w-3.5", !value ? "opacity-100" : "opacity-0")} />
                <span className="text-[var(--text-3)]">— None —</span>
              </CommandItem>
            </CommandGroup>
            {sorted.length > 0 ? (
              <CommandGroup heading="Trades">
                {sorted.map((t) => (
                  <CommandItem
                    key={t.id}
                    value={`${t.name} ${t.csiDivision ?? ""} trade`}
                    onSelect={() => select(t.id)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-3.5 w-3.5",
                        value === t.id ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <span>{t.name}</span>
                    {t.csiDivision ? (
                      <span className="ml-1 text-[var(--text-4)] text-[11px]">
                        · {t.csiDivision}
                      </span>
                    ) : null}
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ── ContactPicker ────────────────────────────────────────────────────

export function ContactPicker({
  value,
  contacts,
  onChange,
  placeholder = "Assign contact…",
}: {
  value: string | null;
  contacts: Contact[];
  onChange: (id: string | null) => void;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);

  const sorted = useMemo(
    () =>
      [...contacts]
        .filter((c) => c.isActive !== false)
        .sort((a, b) => {
          const an = `${a.firstName ?? ""} ${a.lastName ?? ""}`.trim().toLowerCase();
          const bn = `${b.firstName ?? ""} ${b.lastName ?? ""}`.trim().toLowerCase();
          return an.localeCompare(bn);
        }),
    [contacts],
  );

  const selected = value ? sorted.find((c) => c.id === value) : null;
  const label = selected
    ? `${selected.firstName ?? ""} ${selected.lastName ?? ""}`.trim() || "Contact"
    : null;

  function select(id: string | null) {
    onChange(id);
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-auto min-h-[30px] w-full justify-between border-[var(--border-1)] bg-[var(--paper-0)] px-2 py-1 text-[12.5px] font-normal shadow-none hover:bg-[var(--paper-1)]"
        >
          <span className="flex items-center gap-2 truncate">
            {label ? (
              <>
                <Initials name={label} />
                <span className="truncate">{label}</span>
              </>
            ) : (
              <span className="text-[var(--text-4)]">{placeholder}</span>
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start" side="bottom" avoidCollisions>
        <Command>
          <CommandInput placeholder="Search contacts…" />
          <CommandList>
            <CommandEmpty>No contacts found.</CommandEmpty>
            <CommandGroup>
              <CommandItem value="none" onSelect={() => select(null)}>
                <Check className={cn("mr-2 h-3.5 w-3.5", !value ? "opacity-100" : "opacity-0")} />
                <span className="text-[var(--text-3)]">— None —</span>
              </CommandItem>
            </CommandGroup>
            {sorted.length > 0 ? (
              <CommandGroup heading="Contacts">
                {sorted.map((c) => {
                  const name = `${c.firstName ?? ""} ${c.lastName ?? ""}`.trim() || "(No name)";
                  return (
                    <CommandItem
                      key={c.id}
                      value={`${name} ${c.title ?? ""} contact`}
                      onSelect={() => select(c.id)}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-3.5 w-3.5",
                          value === c.id ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <Initials name={name} />
                      <span className="ml-1">{name}</span>
                      {c.title ? (
                        <span className="ml-1 text-[var(--text-4)] text-[11px]">· {c.title}</span>
                      ) : null}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
